import React, { useState } from 'react';
import cn from 'classnames';
import { useRecoilValue } from 'recoil';
import { CallToAction, KeyFeature } from '~/common/models';
import { SvgIcon, Button, FloatingDisclaimer } from '@ui-elements';
import { ModelPageUrl } from '../../store';
import styles from './index.module.scss';

type KFItemProp = {
  text: string;
  disclaimer?: string;
  useDots?: boolean;
};

const KeyFeatureItem: React.FC<KFItemProp> = ({
  text,
  disclaimer,
  useDots,
}) => {
  const htmlText = text.replace(/®/g, '<sup>®</sup>');
  return (
    <div className={styles.keyFeatureItem}>
      {useDots ? (
        <span style={{ width: '10px' }}>•</span>
      ) : (
        <div>
          <SvgIcon type="check" size={0.8} color="#FFFFFF" />
        </div>
      )}

      <FloatingDisclaimer disclaimer={disclaimer}>
        {({ renderTrigger }) => (
          <p>
            <span dangerouslySetInnerHTML={{ __html: htmlText }} />
            {renderTrigger()}
          </p>
        )}
      </FloatingDisclaimer>
    </div>
  );
};

interface CompareRangeKeyFeaturesProps {
  keyFeature: KeyFeature[];
  ctaButton?: CallToAction;
  containerClass?: string;
  customTitle?: React.ReactNode;
  hideShowFullSpecsButton?: boolean;
  defaultDisplayAll?: boolean;
  useDots?: boolean;
}

export const CompareRangeKeyFeatures = React.memo<CompareRangeKeyFeaturesProps>(
  ({
    customTitle,
    keyFeature,
    ctaButton,
    containerClass,
    hideShowFullSpecsButton,
    defaultDisplayAll,
    useDots,
  }) => {
    const [displayAll, setDisplayAll] = useState<boolean>(
      defaultDisplayAll ?? false
    );
    const modelUrl = useRecoilValue(ModelPageUrl);

    return (
      <div className={styles.keyFeaturesWrapper}>
        {customTitle ?? <h3>Key features</h3>}
        <div
          className={cn(styles.keyFeaturesGrid, containerClass, {
            [styles.displayAllKF]: displayAll,
          })}
        >
          {keyFeature.map((kf) => (
            <KeyFeatureItem
              key={kf.id}
              text={kf.description}
              disclaimer={kf.disclaimer}
              useDots={useDots}
            />
          ))}
        </div>

        {!hideShowFullSpecsButton && (
          <div className={styles.fullSpecsFeatWrapper}>
            {!ctaButton?.ctaHidden && (
              <Button
                type="text"
                target={ctaButton?.ctaUrl?.target || '_self'}
                href={ctaButton?.ctaUrl?.url || `/${modelUrl}/specs`}
                className={styles.fullSpecsFeatButton}
              >
                {ctaButton?.ctaUrl?.name || 'View Specs & Features'}
              </Button>
            )}
          </div>
        )}

        {!defaultDisplayAll && (
          <div
            className={styles.kfViewMoreMobile}
            onClick={() => setDisplayAll(!displayAll)}
          >
            {!displayAll ? (
              <>
                <span>View more</span>
                <SvgIcon type="chevronDown" size={0.7} color="#1637a0" />
              </>
            ) : (
              <>
                <span>Show Less</span>
                <SvgIcon type="chevronUp" size={0.7} color="#1637a0" />
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);
